import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  Counter,
  fadeInLeft,
  fadeInRight,
} from "@/components/animations/ScrollAnimations";
import { Target, Eye, Award, Users, Globe, Building, Zap } from "lucide-react";
import { SEO } from "@/components/SEO";
import { CertificationBadge } from "@/components/CertificationBadge";
import { motion } from "framer-motion";

const stats = [
  { label: "EV Drivers", value: 400000, suffix: "+", icon: Users },
  { label: "Charging Locations", value: 2000, suffix: "+", icon: Zap },
  { label: "Countries", value: 13, suffix: "+", icon: Globe },
  { label: "Government Agencies", value: 25, suffix: "+", icon: Building },
];

const certifications = [
  { name: "FedRAMP", description: "Federal Risk and Authorization Management Program", badgeType: "fedramp" as const },
  { name: "SOC 2 Type II", description: "Service Organization Control", badgeType: "soc2" as const },
  { name: "ISO 27001", description: "Information Security Management", badgeType: "iso27001" as const },
  { name: "OCA Certified", description: "Open Charge Alliance Certification", badgeType: "oca" as const },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About EVGateway - EV Charging Network Provider"
        description="EVGateway provides EV charging solutions since 2015. FedRAMP authorized, SOC 2 compliant. 50,000+ drivers, 2,500+ locations."
        keywords="about EVGateway, EV charging company, FedRAMP, SOC 2"
        canonicalUrl="/about"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 text-primary text-sm font-medium mb-6"
              >
                <Building className="h-4 w-4" />
                About EVGateway
              </motion.div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Powering the <span className="text-gradient">Future</span> of
                <br />
                Electric Mobility
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                We're reducing CO2 emissions by accelerating EV adoption through innovative 
                turn-key solutions for installation, maintenance, and network management.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal variants={fadeInLeft}>
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="p-8 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-primary/30 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To accelerate the world's transition to sustainable transportation by providing secure,
                  reliable, and user-friendly EV charging infrastructure. Every charging experience
                  should be as simple as plugging in your phone.
                </p>
              </motion.div>
            </ScrollReveal>

            <ScrollReveal variants={fadeInRight}>
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="p-8 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-accent/30 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-accent" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Vision</h2>
                <p className="text-muted-foreground leading-relaxed">
                  A world where electric vehicles are the norm, supported by a seamless charging network
                  accessible to everyone, everywhere—charging stations as ubiquitous and reliable as gas
                  stations, but cleaner for our planet.
                </p>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-4xl font-bold text-center mb-16">Our Impact</h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <StaggerItem key={stat.label}>
                <motion.div 
                  whileHover={{ scale: 1.03 }}
                  className={`text-center p-6 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 hover:shadow-lg transition-all duration-300 ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <stat.icon className={`w-6 h-6 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <div className={`text-4xl md:text-5xl font-bold mb-2 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`}>
                    <Counter target={stat.value} />
                    {stat.suffix}
                  </div>
                  <div className="text-muted-foreground font-medium">{stat.label}</div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="text-4xl font-bold text-center mb-8">Our Philosophy</h2>
              <p className="text-xl text-center text-muted-foreground mb-12">
                Simplicity and trust are at the core of everything we build.
              </p>
            </ScrollReveal>

            <ScrollReveal>
              <div className="p-8 md:p-10 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We believe EV charging should be as transparent and easy as using GPS. When
                  you pull up to an EVGateway-powered station, you shouldn't need a manual—just plug in and go.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Our platform handles the complexity invisibly: grid management, billing, 
                  authentication, and maintenance—so you don't have to think about it.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Security & Compliance</h2>
              <p className="text-xl text-muted-foreground">
                Industry-leading certifications that demonstrate our commitment to security.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <StaggerItem key={cert.name}>
                <motion.div 
                  whileHover={{ scale: 1.03, y: -5 }}
                  className="p-6 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 text-center group"
                >
                  <div className="flex justify-center mb-4">
                    <CertificationBadge type={cert.badgeType} size="lg" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{cert.name}</h3>
                  <p className="text-sm text-muted-foreground">{cert.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Leadership Team</h2>
              <p className="text-xl text-muted-foreground">
                Industry veterans driving innovation in EV infrastructure.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { name: "Leadership", role: "Executive Team", icon: Award },
              { name: "Engineering", role: "Technical Excellence", icon: Zap },
              { name: "Operations", role: "Global Delivery", icon: Globe },
            ].map((team, idx) => (
              <StaggerItem key={team.name}>
                <motion.div 
                  whileHover={{ scale: 1.03, y: -5 }}
                  className={`p-8 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 text-center transition-all duration-300 hover:shadow-lg ${idx === 1 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${idx === 1 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <team.icon className={`w-10 h-10 ${idx === 1 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{team.name}</h3>
                  <p className="text-muted-foreground">{team.role}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <Footer />
    </div>
  );
}